import { motion } from "framer-motion";
import { Phone, MapPin, ShieldCheck, Copy } from "lucide-react";
import { toast } from "sonner";
import { ADDRESS, PHONE_DISPLAY, PHONE_TEL, RERA } from "@/lib/constants";

export const Contact = () => {
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(ADDRESS);
      toast.success("Address copied to clipboard");
    } catch {
      toast.error("Couldn't copy. Long press to copy manually.");
    }
  };

  return (
    <section id="contact" className="relative py-16 md:py-24 border-t border-white/5" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-10 md:mb-14 max-w-3xl"
        >
          <div className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] mb-4">Contact</div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#F8F6F0] leading-[1.05] tracking-tight">
            Begin your conversation
            <span className="block italic gold-text">with us.</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Call Now - large */}
          <motion.a
            href={PHONE_TEL}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            whileHover={{ y: -4 }}
            data-testid="contact-call-card"
            className="group lg:col-span-2 relative overflow-hidden bg-[#D4AF37] text-black p-6 sm:p-8 md:p-10 min-h-[180px] sm:min-h-[200px] md:min-h-[220px] flex flex-col justify-between btn-luxury w-full text-center lg:text-left"
          >
            <div className="text-sm sm:text-base md:text-[18px] uppercase tracking-[0.4em] opacity-70">Call Now</div>
            <div className="flex flex-col items-center lg:items-start w-full">
              <div className="font-serif text-2xl sm:text-3xl md:text-5xl lg:text-6xl xl:text-7xl leading-none tracking-tight break-words w-full">
                {PHONE_DISPLAY}
              </div>
              <div className="mt-3 sm:mt-4 inline-flex items-center gap-2 sm:gap-3 text-xs sm:text-sm font-medium tracking-[0.2em] uppercase">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 group-hover:rotate-12 transition-transform" />
                Tap to dial · One ring away
              </div>
            </div>
            {/* Decorative */}
            <div className="absolute -bottom-12 -right-12 w-48 sm:w-56 md:w-64 h-48 sm:h-56 md:h-64 rounded-full bg-black/10 blur-3xl" />
          </motion.a>

          {/* Address */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="bg-[#0A0A0B] border border-white/10 hover:border-[#D4AF37]/40 transition-colors p-5 sm:p-6 md:p-8 flex flex-col justify-between min-h-[160px] sm:min-h-[180px] md:min-h-[200px]"
            data-testid="contact-address-card"
          >
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">
                <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                Office Address
              </div>
              <p className="mt-3 sm:mt-4 font-serif text-base sm:text-lg md:text-xl text-[#F8F6F0] leading-snug tracking-tight break-words">
                {ADDRESS}
              </p>
            </div>
            <button
              onClick={copyAddress}
              className="mt-3 sm:mt-4 inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#D4AF37] hover:text-[#F3E5AB] transition-colors self-start"
              data-testid="contact-copy-address"
            >
              <Copy className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Copy address
            </button>
          </motion.div>

          {/* RERA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="bg-[#0A0A0B] border border-white/10 hover:border-[#D4AF37]/40 transition-colors p-5 sm:p-6 md:p-8 flex flex-col justify-between min-h-[160px] sm:min-h-[180px] md:min-h-[200px]"
            data-testid="contact-rera-card"
          >
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">
                <ShieldCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                MahaRERA
              </div>
              <div className="mt-3 sm:mt-4 font-mono text-base sm:text-lg md:text-xl lg:text-2xl text-[#F8F6F0] tracking-widest break-all">
                {RERA}
              </div>
            </div>
            <p className="mt-3 sm:mt-4 text-[10px] sm:text-xs text-[#A0A0A0] font-light leading-relaxed">
              Verified & registered under the Maharashtra Real Estate Regulatory Authority.
            </p>
          </motion.div>

          {/* Project By */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-2 bg-[#0A0A0B] border border-white/10 p-5 sm:p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6 min-h-[120px] sm:min-h-[140px]"
            data-testid="contact-project-by-card"
          >
            <div className="flex items-center gap-4 sm:gap-5">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 border border-[#D4AF37]/60 flex items-center justify-center font-serif text-[#D4AF37] text-xl sm:text-2xl">
                P
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0A0]">Project By</div>
                <div className="font-serif text-xl sm:text-2xl md:text-3xl text-[#F8F6F0]">Prachi Infra</div>
              </div>
            </div>
            <div className="text-[10px] sm:text-xs text-[#A0A0A0] tracking-[0.2em] uppercase text-center sm:text-right">
              Powered by · <span className="text-[#D4AF37]">Ecorb Group</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};